import React from 'react';

// How long an optimistic page survives without the parent confirming it. Must
// clear the 500ms debounce in `useQueryHandlers.onCurrentPageChange` so a
// normal click is never rolled back mid-flight.
export const PAGE_SETTLE_MS = 750;

/**
 * The page a pager should show while the real one is on its way.
 *
 * The page is owned by the parent, but most listings route it through a
 * 500ms debounced URL replace, so rendering `propsPage` alone would leave the
 * pager looking stuck for half a second after every click. The optimistic
 * override covers that gap and is deliberately short-lived: it is dropped the
 * moment `propsPage` lands, and otherwise expires just past the debounce
 * window. The expiry matters because a search/sort/filter issued inside that
 * window strips `page` from the URL — `propsPage` never changes, so nothing
 * else would ever clear the override and the pager would sit on a page the
 * table is no longer showing.
 *
 * While a move is pending, further moves are ignored. The cursor a listing
 * pages with belongs to the page it last fetched, so a second Next before the
 * first lands would ask for page 2 with page 0's cursor: the URL would say
 * page 2 and the rows would be page 1's.
 */
export const useOptimisticPage = (propsPage: number) => {
  const [optimisticPage, setOptimisticPage] = React.useState<number | null>(
    null
  );
  const settleTimer = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  // A ref, not the state: two clicks in one tick both see the state unset.
  const pending = React.useRef(false);

  const settle = React.useCallback(() => {
    clearTimeout(settleTimer.current);
    pending.current = false;
    setOptimisticPage(null);
  }, []);

  React.useEffect(settle, [propsPage, settle]);

  React.useEffect(() => () => clearTimeout(settleTimer.current), []);

  /** Shows `newPage` and runs `commit`, unless an earlier move has not landed yet. */
  const changePage = React.useCallback(
    (newPage: number, commit: (newPage: number) => void) => {
      if (pending.current) {
        return;
      }
      pending.current = true;
      setOptimisticPage(newPage);
      settleTimer.current = setTimeout(settle, PAGE_SETTLE_MS);
      commit(newPage);
    },
    [settle]
  );

  return { page: optimisticPage ?? propsPage, changePage };
};
